import { getAccessToken, setAccessToken, clearAccessToken } from './auth';

// Если REACT_APP_API_BASE задан (прод, Sentry/PostHog) — используем его.
// Иначе (dev без env) берём хост из URL страницы + порт 3900. Это позволяет:
// - localhost:3800 → localhost:3900 (dev как раньше)
// - 192.168.x.x:3800 → 192.168.x.x:3900 (тест с телефона через Wi-Fi)
// - 100.64.x.x:3800 → 100.64.x.x:3900 (тест с телефона через Tailscale)
const API_BASE = process.env.REACT_APP_API_BASE
  || (typeof window !== 'undefined' ? `http://${window.location.hostname}:3900` : 'http://localhost:3900');

// Одна promise для параллельных запросов пока идёт refresh — не дёргаем /refresh 5 раз.
let refreshPromise = null;

const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        clearAccessToken();
        throw new Error('refresh failed');
      }
      const data = await res.json();
      setAccessToken(data.accessToken);
      return data.accessToken;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

// keepalive — браузер доотправит запрос, даже если страница уходит (клик по mailto и т.п.)
const request = async (path, { method = 'GET', body, headers = {}, retryOn401 = true, keepalive = false } = {}) => {
  const token = getAccessToken();
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const finalHeaders = { ...headers };
  // Для multipart (FormData) Content-Type ставит сам браузер (с boundary) — не трогаем.
  if (!isForm) finalHeaders['Content-Type'] = 'application/json';
  if (token) finalHeaders.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: finalHeaders,
    credentials: 'include',
    keepalive,
    body: body ? (isForm ? body : JSON.stringify(body)) : undefined,
  });

  if (res.status === 401 && retryOn401) {
    try {
      await refreshAccessToken();
      return request(path, { method, body, headers, retryOn401: false, keepalive });
    } catch {
      // refresh провалился — пробрасываем оригинальную 401
    }
  }

  const contentType = res.headers.get('content-type') || '';
  const payload = contentType.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    // Сервер иногда шлёт error как строку ({error: "текст"}), иногда как объект ({error: {message, code}}).
    const errMsg =
      (typeof payload?.error === 'string' ? payload.error : payload?.error?.message) ||
      payload?.message ||
      res.statusText;
    const err = new Error(errMsg);
    err.status = res.status;
    err.code = payload?.code || payload?.error?.code;
    err.payload = payload;
    throw err;
  }

  return payload;
};

export const api = {
  get: (path, opts) => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
  put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
  del: (path, opts) => request(path, { ...opts, method: 'DELETE' }),
};

// Диагностика: «Написать в поддержку» — снимок с браузера уходит туда же, куда из
// приложения (DiagReporter.SendReport), и админу сразу приходит пуш (supportAlert).
export const diag = {
  // keepalive: сразу после клика открывается почта — запрос не должен оборваться (Astra 29.10)
  report: (body) => api.post('/api/diag/report', body, { keepalive: true }),
};

export const rooms = {
  // П.3: body { botNames: [3 имени] } — соло-«Реванш» с теми же ботами; без body — новые имена.
  createSolo: (body) => api.post('/api/rooms/solo', body || {}),
  // ranked=true — рейтинговая игра: стол ждёт только живых, ботов не подсаживает
  // (решение Адека 13.09.2026). Без флага — прежний быстрый старт.
  quickStart: (ranked = false) => api.post('/api/rooms/quick-start', ranked ? { ranked: true } : undefined),
  list: () => api.get('/api/rooms?hasSlots=true'),
  get: (id) => api.get(`/api/rooms/${id}`),
  getByCode: (code) => api.get(`/api/rooms/by-code/${code}`),
  create: (body) => api.post('/api/rooms', body),
  join: (id, slot) => api.post(`/api/rooms/${id}/join`, { slot }),
  leave: (id) => api.post(`/api/rooms/${id}/leave`),
  changeSeat: (id, slot) => api.post(`/api/rooms/${id}/seat`, { slot }),
  ready: (id, isReady) => api.post(`/api/rooms/${id}/ready`, { ready: isReady }),
  addBot: (id, slot, difficulty = 'hard') => api.post(`/api/rooms/${id}/add-bot`, { slot, difficulty }),
  kick: (id, slot) => api.post(`/api/rooms/${id}/kick`, { slot }),
  close: (id) => api.del(`/api/rooms/${id}`),
};

export const games = {
  active: () => api.get('/api/games/active'),
  get: (id) => api.get(`/api/games/${id}`),
};

// Витрина и права игрока. Флаги функций приходят С СЕРВЕРА, а не зашиты в клиент:
// каталог живёт в shared/monetization.js, но CRA не пускает импорт вне src — и это
// к лучшему, флаг переключается без пересборки приложения.
export const billing = {
  catalog: () => api.get('/api/billing/catalog'),
  me: () => api.get('/api/billing/me'),
  // Что у меня есть и что надето. Список приходит с сервера: сборка знает только КАК
  // вещь выглядит, а можно ли её надеть — решает сервер.
  cosmetics: () => api.get('/api/billing/cosmetics'),
  // Надеть вещь; itemId: null — снять. Сервер тут же рассылает смену за стол, если
  // человек уже играет.
  setWorn: (slot, itemId) => api.put('/api/billing/worn', { slot, itemId }),
  // КАССА БЕТЫ: покупка без стора и без денег. Работает, только пока сервер сам
  // говорит `betaCheckout: true` в каталоге (флаг + переменная окружения на сервере).
  // Нужна, чтобы пройти весь путь покупки живьём до подключения RevenueCat.
  devBuy: (productId) => api.post('/api/billing/dev-buy', { productId }),
};

export const admin = {
  // Гейт: 200 = админ, 403 = нет. Проверяет настоящую роль из БД.
  ping: () => api.get('/api/admin/ping', { retryOn401: false }),
  // Снапшот live-состояния (fallback к socket-push на namespace /admin).
  live: () => api.get('/api/admin/live'),
  // Complaints/Logs: таймлайн событий (поиск/фильтр/курсор) + резолв пользователя.
  events: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ''),
    ).toString();
    return api.get(`/api/admin/events${q ? `?${q}` : ''}`);
  },
  resolveUser: (key) => api.get(`/api/admin/users/${encodeURIComponent(key)}`),
  analytics: () => api.get('/api/admin/analytics'),
  players: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ''),
    ).toString();
    return api.get(`/api/admin/players${qs ? `?${qs}` : ''}`);
  },
  health: () => api.get('/api/admin/health'),
  // п.73 UGC: жалобы игроков + меры
  reports: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ''),
    ).toString();
    return api.get(`/api/admin/reports${qs ? `?${qs}` : ''}`);
  },
  updateReport: (id, body) => request(`/api/admin/reports/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }),
  ban: (userId, body) => api.post(`/api/admin/users/${userId}/ban`, body),
  // Мягкая санкция: игрок продолжает играть, но его камера и микрофон не включаются
  videoBan: (userId, body) => api.post(`/api/admin/users/${userId}/video-ban`, body),
};

// Уведомления: список, счётчик непрочитанного и отметка «прочитано». Живое новое
// приходит событием notification:new на сокете — опроса по кругу здесь нет.
export const notifications = {
  list: (limit) => api.get(`/api/notifications${limit ? `?limit=${limit}` : ''}`),
  unreadCount: () => api.get('/api/notifications/unread-count'),
  markRead: (ids) => api.post('/api/notifications/read', ids ? { ids } : {}),
  clear: () => api.del('/api/notifications'),   // «Очистить» — убрать всё из колокольчика
};

export const users = {
  // Таблица лучших: два зачёта (сезон и сила) + своё место. Тот же ответ, что читает
  // приложение — экран «Рейтинг» на сайте обязан показывать ТО ЖЕ, что и в APK.
  leaderboard: (limit) => api.get(`/api/users/leaderboard${limit ? `?limit=${limit}` : ''}`),
  // Загрузка аватара — multipart, поле "avatar". Возвращает { avatar, avatarStyle }.
  uploadAvatar: (file) => {
    const fd = new FormData();
    fd.append('avatar', file);
    return request('/api/users/me/avatar', { method: 'POST', body: fd });
  },
  deleteAvatar: () => api.del('/api/users/me/avatar'),
  publicProfile: (id) => api.get(`/api/users/${id}/public`),
  // п.73 UGC: жалоба на игрока. body: { reason, comment?, gameId?, roomId? }
  report: (id, body) => api.post(`/api/users/${id}/report`, body),
  // п.69: удаление аккаунта. С паролем → { password }; соц/гость → { confirm: 'УДАЛИТЬ' }.
  deleteMe: (body) => api.del('/api/users/me', { body }),
};

export const friends = {
  list: () => api.get('/api/friends'),
  requests: () => api.get('/api/friends/requests'),
  search: (q) => api.get(`/api/friends/search?q=${encodeURIComponent(q)}`),
  myCode: () => api.get('/api/friends/my-code'),
  recentOpponents: () => api.get('/api/friends/recent-opponents'),
  sendRequest: (targetId) => api.post('/api/friends/requests', { targetId }),
  accept: (id) => api.post(`/api/friends/requests/${id}/accept`),
  reject: (id) => api.post(`/api/friends/requests/${id}/reject`),
  cancel: (id) => api.del(`/api/friends/requests/${id}`),
  addByCode: (code) => api.post('/api/friends/by-code', { code }),
  remove: (userId) => api.del(`/api/friends/${userId}`),
  block: (userId) => api.post(`/api/friends/${userId}/block`),
  unblock: (userId) => api.del(`/api/friends/${userId}/block`),
  // Позвать друга за стол. roomId — позвать в мою существующую комнату; без него сервер создаёт приватную.
  inviteToTable: (userId, roomId) => api.post(`/api/friends/${userId}/invite`, roomId ? { roomId } : {}),
  acceptInvite: (inviteId) => api.post(`/api/friends/invites/${inviteId}/accept`),
  declineInvite: (inviteId) => api.post(`/api/friends/invites/${inviteId}/decline`),
};

export const auth = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  // Соц-логин + гость (сервер: socialAuthController). google/facebook без ключей → 503.
  google: (body) => api.post('/api/auth/google', body), // { accessToken } (web) или { idToken } (mobile)
  facebook: (accessToken) => api.post('/api/auth/facebook', { accessToken }),
  guest: (deviceId) => api.post('/api/auth/guest', { deviceId }),
  devLogin: () => api.post('/api/auth/dev-login'),   // DEV-only автологин (сервер пустит лишь при DEV_AUTOLOGIN=1)
  logout: () => api.post('/api/auth/logout'),
  refresh: refreshAccessToken,
  me: () => api.get('/api/auth/me'),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),
  // Сервер ждёт { email, code, newPassword } — код из письма, не токен из ссылки
  // (authController.js:471). Прежняя подпись (token, password) молча ломала сброс
  // пароля на сайте: сервер отбивал запрос как MISSING_FIELDS. Починено 23.09.2026.
  resetPassword: (email, code, newPassword) => api.post('/api/auth/reset-password', { email, code, newPassword }),
  verifyEmail: (token) => api.get(`/api/auth/verify-email?token=${encodeURIComponent(token)}`, { retryOn401: false }),
  resendVerification: () => api.post('/api/auth/resend-verification'),
  changePassword: (currentPassword, newPassword) => api.post('/api/auth/change-password', { currentPassword, newPassword }),   // п.69
  logoutAll: () => api.post('/api/auth/logout-all'),
};
